import { IntendedDisplay } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type {
  BannerCarouselSlide,
  WebCustomImageAdEvent,
} from '@iheartjane/menu';
import {
  BannerCarousel,
  imageAdTrackingPayload,
  useIsModalOpen,
} from '@iheartjane/menu';
import { Box, Skeleton, useMobileMediaQuery } from '@iheartjane/reefer';
import { useLocationOrigin } from '@iheartjane/utils';
import { useCallback, useMemo, useState } from 'react';

import { useSearchContext } from '@jane/search/hooks';
import { useScrollToElement } from '@jane/shared-ecomm/hooks';
import { useMenu, useMenuAds } from '@jane/shared-ecomm/providers';
import { track } from '@jane/shared-ecomm/tracking';

import { MENU_RESULTS_ELEMENT_ID } from '../../MenuProductsGrid/MenuProductsGrid';
import {
  buildCustomImageAdSlide,
  getMobileCarouselSlides,
  storeHasVisibleCarousel,
  toBannerCarouselSlides,
} from './MenuBannerCarousel.util';

export const MenuBannerCarousel = () => {
  const origin = useLocationOrigin();
  const { paths, store } = useMenu();
  const isMobile = useMobileMediaQuery({});
  const placeSecond = useFlag(FLAGS.p1PlaceImageAdSecond, false);
  const { searchState, setSearchState } = useSearchContext();

  const isModalOpen = useIsModalOpen();
  const scrollToResults = useScrollToElement(MENU_RESULTS_ELEMENT_ID);

  const ads = useMenuAds();
  const instance = ads?.imageAd;
  const isLoading = ads?.isLoading ?? false;

  const storeSlides = useMemo<BannerCarouselSlide[]>(() => {
    if (!storeHasVisibleCarousel(store)) return [];

    const desktopCarouselSlides =
      store?.carousel_banner?.filter((i) => i.breakpoint === 'desktop') || [];

    const mobileCarouselSlides =
      store?.carousel_banner?.filter((i) => i.breakpoint === 'mobile') || [];

    return toBannerCarouselSlides(
      isMobile
        ? getMobileCarouselSlides(mobileCarouselSlides, desktopCarouselSlides)
        : desktopCarouselSlides,
      store?.name
    );
  }, [isMobile, store]);

  const intendedDisplay = isMobile
    ? IntendedDisplay.Mobile
    : IntendedDisplay.Desktop;
  const adPosition = placeSecond && storeSlides.length > 0 ? 2 : 1;
  const adBrandName = instance?.products[0]?.attributes.brand ?? '';

  const [viewedInstances] = useState(() => new WeakSet<object>());

  const trackAdEvent = useCallback(
    (event: WebCustomImageAdEvent) => {
      // once per ad instance, matching the SDK's own impression dedupe
      if (event.type === 'viewed_ad' && instance) {
        if (viewedInstances.has(instance)) return;

        viewedInstances.add(instance);
      }

      track(
        imageAdTrackingPayload(event, {
          adPosition,
          brandName: adBrandName,
          intendedDisplay,
          menuType: 'boost',
          storeHasBanners: storeSlides.length > 0,
          storeId: store?.id,
        })
      );
    },
    [
      adPosition,
      adBrandName,
      instance,
      intendedDisplay,
      store?.id,
      storeSlides,
      viewedInstances,
    ]
  );

  const adSlide = useMemo(
    () =>
      buildCustomImageAdSlide({
        instance,
        intendedDisplay,
        onEvent: trackAdEvent,
        onImageClick: (brandName) => {
          if (!brandName) return;

          setSearchState({
            filters: { ...searchState.filters, brand: [brandName] },
          });

          scrollToResults();
        },
        toPdpUrl: ({ brand, name, product_id }) =>
          paths.menuProduct({ brand, id: product_id, name }),
      }),
    [
      instance,
      intendedDisplay,
      paths,
      scrollToResults,
      searchState,
      setSearchState,
      trackAdEvent,
    ]
  );

  const slides = useMemo<BannerCarouselSlide[]>(() => {
    if (adSlide == null) return storeSlides;

    return [
      ...storeSlides.slice(0, adPosition - 1),
      adSlide,
      ...storeSlides.slice(adPosition - 1),
    ];
  }, [adPosition, adSlide, storeSlides]);

  const handleView = useCallback(
    (slide: BannerCarouselSlide) => {
      if (slide?.kind !== 'customImageAd') return;

      instance?.products.forEach((product) => void product.impress());
    },
    [instance]
  );

  // show skeleton when loading to avoid jumps during filter & category changes
  if (isLoading) {
    return (
      <Box px={isMobile ? 24 : 64}>
        <Skeleton animate>
          <Skeleton.Bone
            data-testid="banner-carousel-skeleton"
            height="auto"
            style={{ aspectRatio: isMobile ? '160 / 39' : '149 / 36' }}
          />
        </Skeleton>
      </Box>
    );
  }

  if (!slides.length) return null;

  return (
    <Box px={isMobile ? 24 : 64}>
      <BannerCarousel
        key={isMobile ? 'mobile' : 'desktop'}
        disableAutoAdvance={isModalOpen}
        onView={handleView}
        slides={slides}
        comparisonUrl={origin}
      />
    </Box>
  );
};
